import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { getEmployeeById } from '../../admin/services/employeeService';
import type { Employee } from '../../admin/types/Employee';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { getAssetById } from '../../assets/services/assetService';
import type { Asset } from '../../assets/types/Asset';
import { getContactById } from '../../contacts/services/contactService';
import type { Contact } from '../../contacts/types/Contact';
import { getEventById } from '../../calendar/services/calendarService';
import type { CalendarEvent, AppointmentType } from '../../calendar/types/Calendar';
import {
  addConsultationFieldAssignment,
  addConsultationFieldValue,
  approveConsultation,
  deleteConsultation,
  getConsultationById,
  getConsultationTemplateById,
  listConsultationFieldAssignments,
  listConsultationFieldDefinitions,
  listConsultationStatuses,
  listConsultationFieldValues,
  listConsultationTemplates,
  listConsultationTypes,
  listConsultations,
  rejectConsultation,
  removeConsultationFieldAssignment,
  removeConsultationFieldValue,
  updateConsultation,
  updateConsultationFieldValue,
} from '../services/consultationService';
import type {
  Consultation,
  ConsultationFieldDefinition,
  ConsultationStatusDefinition,
  ConsultationFieldValue,
  ConsultationTemplate,
} from '../types/Consultation';
import {
  buildStructuredConsultationFieldValues,
  formatConsultationAuditSummary,
  getMissingRequiredConsultationFieldIds,
  getMissingRequiredConsultationFields,
  getRelatedConsultationSwitcherLabel,
  getSectionKeysWithMissingRequiredFields,
  mergeStructuredConsultationFields,
  normalizeStructuredConsultationFieldValueForStorage,
  StructuredConsultationField,
  validateStructuredConsultationFieldValue,
} from '../consultationUtils';
import { ConsultationAddFieldPanel } from './ConsultationAddFieldPanel';
import { ConsultationContextHeader, resolveConsultationStatusDefinition } from './ConsultationContextHeader';
import { ConsultationCoreFieldsForm } from './ConsultationCoreFieldsForm';
import { ConsultationExportMenu } from './ConsultationExportMenu';
import { ConsultationRecordShell } from './ConsultationRecordShell';
import { ConsultationStickyActionBar } from './ConsultationStickyActionBar';
import { ConsultationStructuredFieldsPanel } from './ConsultationStructuredFieldsPanel';
import shellStyles from './ConsultationRecordShell.module.css';
import styles from './ConsultationDetailsPage.module.css';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const humanizeLabel = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed || GUID_PATTERN.test(trimmed)) {
    return '';
  }

  return trimmed;
};

export const ConsultationDetailsPage: React.FC = () => {
  const { t } = useTranslation(['consultations', 'calendar', 'common', 'assets', 'nav']);
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { consultationId } = useParams<{ consultationId: string }>();
  const canManageConsultations = hasPermission(PermissionScopes.consultationsManage);
  const canDeleteConsultations = hasPermission(PermissionScopes.consultationsDelete);
  const canAssignConsultationFields = hasPermission(PermissionScopes.consultationsFieldAssignmentsManage);
  const canOpenCalendarConsultation = hasPermission(PermissionScopes.calendarRead);
  const consultationsListPath = `/consultations${location.search}`;
  const returnTo = React.useMemo(() => {
    const state = location.state as { returnTo?: string } | null;
    return state?.returnTo;
  }, [location.state]);
  const backPath = returnTo || consultationsListPath;
  const backLabel = React.useMemo(() => {
    if (returnTo?.startsWith('/calendar')) {
      return t('calendar', { ns: 'nav', defaultValue: 'Calendar' });
    }

    return t('consultations', { defaultValue: 'Consultations' });
  }, [returnTo, t]);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);
  const [consultation, setConsultation] = React.useState<Consultation | null>(null);
  const [relatedConsultations, setRelatedConsultations] = React.useState<Consultation[]>([]);
  const [asset, setAsset] = React.useState<Asset | null>(null);
  const [contact, setContact] = React.useState<Contact | null>(null);
  const [event, setEvent] = React.useState<CalendarEvent | null>(null);
  const [eventAsset, setEventAsset] = React.useState<Asset | null>(null);
  const [eventContact, setEventContact] = React.useState<Contact | null>(null);
  const [template, setTemplate] = React.useState<ConsultationTemplate | null>(null);
  const [fieldDefinitions, setFieldDefinitions] = React.useState<ConsultationFieldDefinition[]>([]);
  const [consultationStatuses, setConsultationStatuses] = React.useState<ConsultationStatusDefinition[]>([]);
  const [fields, setFields] = React.useState<StructuredConsultationField[]>([]);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = React.useState(false);
  const [eventStaff, setEventStaff] = React.useState<Employee[]>([]);
  const [assignmentSaving, setAssignmentSaving] = React.useState(false);
  const [savedFieldValues, setSavedFieldValues] = React.useState<ConsultationFieldValue[]>([]);
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>({});
  const [templates, setTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [appointmentTypeId, setAppointmentTypeId] = React.useState('');
  const [templateId, setTemplateId] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [summary, setSummary] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [loadError, setLoadError] = React.useState('');
  const [statusMessage, setStatusMessage] = React.useState('');
  const [statusError, setStatusError] = React.useState('');
  const [forcedActiveSectionKey, setForcedActiveSectionKey] = React.useState<string | null>(null);
  const [highlightedFieldIds, setHighlightedFieldIds] = React.useState<ReadonlySet<string>>(new Set());
  const baselineRef = React.useRef<{
    notes: string;
    summary: string;
    status: string;
    appointmentTypeId: string;
    templateId: string;
    fieldValues: Record<string, string>;
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!consultationId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setNotFound(false);
      setLoadError('');
      setConsultation(null);

      try {
        const loadedConsultation = await getConsultationById(consultationId);
        if (cancelled) return;

        if (!loadedConsultation) {
          setNotFound(true);
          setConsultation(null);
          return;
        }

        setConsultation(loadedConsultation);
        setRelatedConsultations([]);
        setNotes(loadedConsultation.notes ?? '');
        setSummary(loadedConsultation.summary ?? '');
        setStatus(loadedConsultation.status ?? '');
        setAppointmentTypeId(loadedConsultation.appointmentTypeId ?? '');
        setTemplateId(loadedConsultation.templateId ?? '');

        try {
          const contactId = loadedConsultation.customerContactId || loadedConsultation.contactId;
          const linkedEventId = loadedConsultation.eventId || loadedConsultation.calendarEventId;
          const [loadedAsset, loadedContact, loadedEvent, loadedTemplate, loadedValues, loadedStatuses, loadedAssignments, loadedDefinitions, loadedAppointmentTypes, loadedRelated] = await Promise.all([
            loadedConsultation.assetId ? getAssetById(loadedConsultation.assetId) : Promise.resolve(null),
            contactId ? getContactById(contactId) : Promise.resolve(null),
            linkedEventId ? getEventById(linkedEventId) : Promise.resolve(null),
            loadedConsultation.templateId ? getConsultationTemplateById(loadedConsultation.templateId) : Promise.resolve(null),
            listConsultationFieldValues(loadedConsultation.id),
            listConsultationStatuses(),
            listConsultationFieldAssignments(loadedConsultation.id),
            listConsultationFieldDefinitions({ isActive: true }),
            listConsultationTypes({ status: 'active' }),
            linkedEventId
              ? listConsultations({ eventId: linkedEventId, page: 1, pageSize: 100 })
              : Promise.resolve([loadedConsultation]),
          ]);

          if (cancelled) return;

          const nextFields = mergeStructuredConsultationFields(loadedTemplate, loadedDefinitions, loadedAssignments);
          const initialFieldValues = buildStructuredConsultationFieldValues(nextFields, loadedValues);

          setAsset(loadedAsset);
          setContact(loadedContact);
          setEvent(loadedEvent);
          setEventAsset(null);
          setEventContact(null);
          setTemplate(loadedTemplate);
          setAppointmentTypes(loadedAppointmentTypes);
          setFieldDefinitions(loadedDefinitions);
          setConsultationStatuses(loadedStatuses);
          setFields(nextFields);
          setSavedFieldValues(loadedValues);
          setFieldValues(initialFieldValues);
          const relatedItems = (loadedRelated || []).filter((item) => !item.isDeleted);
          setRelatedConsultations(
            relatedItems.some((item) => item.id === loadedConsultation.id)
              ? relatedItems
              : [loadedConsultation, ...relatedItems],
          );
          baselineRef.current = {
            notes: loadedConsultation.notes ?? '',
            summary: loadedConsultation.summary ?? '',
            status: loadedConsultation.status ?? '',
            appointmentTypeId: loadedConsultation.appointmentTypeId ?? '',
            templateId: loadedConsultation.templateId ?? '',
            fieldValues: initialFieldValues,
          };
        } catch (detailError) {
          if (cancelled) return;

          setAsset(null);
          setContact(null);
          setEvent(null);
          setEventAsset(null);
          setEventContact(null);
          setTemplate(null);
          setAppointmentTypes([]);
          setFieldDefinitions([]);
          setConsultationStatuses([]);
          setFields([]);
          setSavedFieldValues([]);
          setFieldValues({});
          setRelatedConsultations([loadedConsultation]);
          setLoadError(detailError instanceof Error
            ? detailError.message
            : t('consultationLoadFailed', { defaultValue: 'Failed to load consultation details.' }));
        }
      } catch (error) {
        if (cancelled) return;

        if (error instanceof Error && /404|not found/i.test(error.message)) {
          setNotFound(true);
          setConsultation(null);
          setRelatedConsultations([]);
          return;
        }

        setConsultation(null);
        setRelatedConsultations([]);
        setLoadError(error instanceof Error
          ? error.message
          : t('consultationLoadFailed', { defaultValue: 'Failed to load consultation details.' }));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [consultationId, t]);

  const availableStatuses = React.useMemo(() => {
    if (!status || consultationStatuses.some((item) => item.name === status)) {
      return consultationStatuses;
    }

    return [
      {
        id: `legacy-${status}`,
        name: status,
        displayOrder: -1,
        isDefault: false,
        isCompleted: false,
      },
      ...consultationStatuses,
    ];
  }, [consultationStatuses, status]);

  const consultationTypeName = React.useMemo(() => {
    if (appointmentTypeId) {
      return appointmentTypes.find((item) => item.id === appointmentTypeId)?.name;
    }

    if (!consultation) {
      return undefined;
    }

    if (consultation.appointmentTypeName?.trim()) {
      return consultation.appointmentTypeName;
    }

    return undefined;
  }, [appointmentTypeId, appointmentTypes, consultation]);

  const consultationCreationMode = React.useMemo(() => {
    if (!appointmentTypeId) {
      return undefined;
    }

    return appointmentTypes.find((item) => item.id === appointmentTypeId)?.consultationCreationMode;
  }, [appointmentTypes, appointmentTypeId]);

  const [relatedEmployeeNames, setRelatedEmployeeNames] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (consultationCreationMode !== 'perEmployee') {
      setRelatedEmployeeNames({});
      return;
    }

    const employeeIds = Array.from(new Set(
      relatedConsultations
        .map((item) => item.assignedEmployeeId)
        .filter((employeeId): employeeId is string => Boolean(employeeId)),
    ));
    const missingEmployeeIds = employeeIds.filter(
      (employeeId) => !eventStaff.some((employee) => employee.id === employeeId),
    );

    if (missingEmployeeIds.length === 0) {
      setRelatedEmployeeNames({});
      return;
    }

    let cancelled = false;

    void Promise.all(missingEmployeeIds.map((employeeId) => getEmployeeById(employeeId)))
      .then((employees) => {
        if (cancelled) {
          return;
        }

        const nextNames: Record<string, string> = {};
        employees.forEach((employee) => {
          if (employee?.id) {
            nextNames[employee.id] = employee.name;
          }
        });
        setRelatedEmployeeNames(nextNames);
      })
      .catch(() => {
        if (!cancelled) {
          setRelatedEmployeeNames({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [consultationCreationMode, eventStaff, relatedConsultations]);

  const employeeNameById = React.useMemo(() => {
    const names: Record<string, string> = { ...relatedEmployeeNames };
    eventStaff.forEach((employee) => {
      names[employee.id] = employee.name;
    });
    return names;
  }, [eventStaff, relatedEmployeeNames]);

  const isDirty = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) {
      return false;
    }

    if (notes !== baseline.notes || summary !== baseline.summary || status !== baseline.status) {
      return true;
    }

    if (templateId !== baseline.templateId) {
      return true;
    }

    if (appointmentTypeId !== baseline.appointmentTypeId) {
      return true;
    }

    const fieldIds = Array.from(new Set([...Object.keys(baseline.fieldValues), ...Object.keys(fieldValues)]));
    for (const fieldId of fieldIds) {
      if ((fieldValues[fieldId] ?? '') !== (baseline.fieldValues[fieldId] ?? '')) {
        return true;
      }
    }

    return false;
  }, [appointmentTypeId, fieldValues, notes, status, summary, templateId]);

  React.useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  React.useEffect(() => {
    if (!isEventDetailsOpen) {
      return undefined;
    }

    const handleKeyDown = (eventValue: KeyboardEvent) => {
      if (eventValue.key === 'Escape') {
        setIsEventDetailsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEventDetailsOpen]);

  React.useEffect(() => {
    let cancelled = false;

    const loadEventStaff = async () => {
      const staffIds = event?.staffIds && event.staffIds.length > 0
        ? event.staffIds
        : (event?.staffId ? [event.staffId] : []);

      if (staffIds.length === 0) {
        setEventStaff([]);
        return;
      }

      try {
        const employees = await Promise.all(staffIds.map((staffId) => getEmployeeById(staffId)));
        if (!cancelled) {
          setEventStaff(employees.filter((employee): employee is Employee => Boolean(employee)));
        }
      } catch {
        if (!cancelled) {
          setEventStaff([]);
        }
      }
    };

    void loadEventStaff();

    return () => {
      cancelled = true;
    };
  }, [event]);

  React.useEffect(() => {
    let cancelled = false;

    const loadEventRelations = async () => {
      const eventAssetId = event?.assetIds && event.assetIds.length > 0
        ? event.assetIds[0]
        : event?.assetId;
      const eventContactId = event?.contactId;

      if (!eventAssetId && !eventContactId) {
        setEventAsset(null);
        setEventContact(null);
        return;
      }

      try {
        const [loadedEventAsset, loadedEventContact] = await Promise.all([
          eventAssetId ? getAssetById(eventAssetId) : Promise.resolve(null),
          eventContactId ? getContactById(eventContactId) : Promise.resolve(null),
        ]);

        if (!cancelled) {
          setEventAsset(loadedEventAsset);
          setEventContact(loadedEventContact);
        }
      } catch {
        if (!cancelled) {
          setEventAsset(null);
          setEventContact(null);
        }
      }
    };

    void loadEventRelations();

    return () => {
      cancelled = true;
    };
  }, [event]);

  React.useEffect(() => {
    if (!appointmentTypeId) {
      setTemplates([]);
      return;
    }

    let cancelled = false;

    const loadTemplates = async () => {
      const loadedTemplates = await listConsultationTemplates({
        isActive: true,
        appointmentTypeId,
      });

      if (!cancelled) {
        setTemplates(loadedTemplates);
      }
    };

    void loadTemplates();

    return () => {
      cancelled = true;
    };
  }, [appointmentTypeId]);

  const onFieldChange = React.useCallback((fieldId: string, value: string) => {
    setFieldValues((current) => ({ ...current, [fieldId]: value }));
  }, []);

  const refreshStructuredFields = React.useCallback(async (activeConsultation: Consultation, activeTemplate: ConsultationTemplate | null) => {
    const [loadedAssignments, loadedValues] = await Promise.all([
      listConsultationFieldAssignments(activeConsultation.id),
      listConsultationFieldValues(activeConsultation.id),
    ]);
    const nextFields = mergeStructuredConsultationFields(activeTemplate, fieldDefinitions, loadedAssignments);
    setFields(nextFields);
    setSavedFieldValues(loadedValues);
    setFieldValues(buildStructuredConsultationFieldValues(nextFields, loadedValues));
  }, [fieldDefinitions]);

  const onTemplateChange = React.useCallback(async (nextTemplateId: string) => {
    if (!consultation) {
      return;
    }

    setTemplateId(nextTemplateId);

    let nextTemplate: ConsultationTemplate | null = null;
    if (nextTemplateId) {
      nextTemplate = await getConsultationTemplateById(nextTemplateId);
    }

    setTemplate(nextTemplate);

    const loadedAssignments = await listConsultationFieldAssignments(consultation.id);
    const nextFields = mergeStructuredConsultationFields(nextTemplate, fieldDefinitions, loadedAssignments);
    setFields(nextFields);
    setFieldValues((current) => {
      const next = buildStructuredConsultationFieldValues(nextFields, savedFieldValues);
      nextFields.forEach((field) => {
        if (current[field.id] !== undefined) {
          next[field.id] = current[field.id];
        }
      });
      return next;
    });
  }, [consultation, fieldDefinitions, savedFieldValues]);

  const onAppointmentTypeChange = React.useCallback(async (nextAppointmentTypeId: string) => {
    setAppointmentTypeId(nextAppointmentTypeId);

    if (!nextAppointmentTypeId) {
      if (templateId) {
        await onTemplateChange('');
      }
      return;
    }

    const loadedTemplates = await listConsultationTemplates({
      isActive: true,
      appointmentTypeId: nextAppointmentTypeId,
    });
    setTemplates(loadedTemplates);

    if (templateId && !loadedTemplates.some((item) => item.id === templateId)) {
      await onTemplateChange('');
      setStatusError('');
      setStatusMessage(t('consultationTypeChangeClearsTemplate', {
        defaultValue: 'Consultation type changed. The previous template was removed because it does not belong to this type.',
      }));
    }
  }, [onTemplateChange, t, templateId]);

  const handleAddConsultationField = React.useCallback(async (fieldDefinitionId: string, isRequired: boolean) => {
    if (!canAssignConsultationFields || !consultation) return;

    setAssignmentSaving(true);
    setStatusMessage('');
    setStatusError('');

    try {
      const maxSortOrder = fields.reduce((max, field) => Math.max(max, field.assignmentSortOrder), 0);
      const created = await addConsultationFieldAssignment(consultation.id, {
        fieldDefinitionId,
        isRequired,
        sortOrder: maxSortOrder + 1,
      });
      if (!created) {
        setStatusError(t('consultationFieldAssignmentFailed', { defaultValue: 'Failed to add field to consultation.' }));
        return;
      }

      await refreshStructuredFields(consultation, template);
      setStatusMessage(t('consultationFieldAdded', { defaultValue: 'Field added to consultation.' }));
    } catch (addError) {
      setStatusError(addError instanceof Error ? addError.message : t('consultationFieldAssignmentFailed', { defaultValue: 'Failed to add field to consultation.' }));
    } finally {
      setAssignmentSaving(false);
    }
  }, [canAssignConsultationFields, consultation, fields, refreshStructuredFields, t, template]);

  const handleRemoveConsultationField = React.useCallback(async (fieldDefinitionId: string) => {
    if (!canAssignConsultationFields || !consultation) return;

    setAssignmentSaving(true);
    setStatusMessage('');
    setStatusError('');

    try {
      await removeConsultationFieldAssignment(consultation.id, fieldDefinitionId);
      await refreshStructuredFields(consultation, template);
      setStatusMessage(t('consultationFieldRemoved', { defaultValue: 'Field removed from consultation.' }));
    } catch (removeError) {
      setStatusError(removeError instanceof Error ? removeError.message : t('consultationFieldRemoveFailed', { defaultValue: 'Failed to remove field from consultation.' }));
    } finally {
      setAssignmentSaving(false);
    }
  }, [canAssignConsultationFields, consultation, refreshStructuredFields, t, template]);

  const handleCopyLink = React.useCallback(async () => {
    setStatusMessage('');
    setStatusError('');

    if (!navigator.clipboard?.writeText) {
      setStatusError(t('copyLinkNotSupported', { defaultValue: 'Copy link is not supported in this browser.' }));
      return;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      setStatusMessage(t('consultationLinkCopied', { defaultValue: 'Consultation link copied.' }));
    } catch {
      setStatusError(t('consultationLinkCopyFailed', { defaultValue: 'Failed to copy consultation link.' }));
    }
  }, [t]);

  const handleSave = React.useCallback(async () => {
    if (!canManageConsultations) return;
    if (!consultation) return;

    if (!appointmentTypeId) {
      setStatusMessage('');
      setStatusError(t('consultationTypeRequired', { defaultValue: 'Consultation type is required.' }));
      return;
    }

    const missingRequiredFields = getMissingRequiredConsultationFields(fields, fieldValues);
    if (missingRequiredFields.length > 0) {
      const missingFieldIds = getMissingRequiredConsultationFieldIds(fields, fieldValues);
      const sectionKeys = getSectionKeysWithMissingRequiredFields(fields, fieldValues);
      setHighlightedFieldIds(new Set(missingFieldIds));
      setForcedActiveSectionKey(sectionKeys[0] ?? null);
      setStatusMessage('');
      setStatusError(t('consultationMissingRequiredFields', {
        defaultValue: 'Complete the required structured fields: {{fields}}.',
        fields: missingRequiredFields.join(', '),
      }));
      return;
    }

    for (const field of fields) {
      const validationError = validateStructuredConsultationFieldValue(field, fieldValues[field.id]);
      if (validationError) {
        setHighlightedFieldIds(new Set([field.id]));
        setForcedActiveSectionKey(getSectionKeysWithMissingRequiredFields([field], fieldValues)[0] ?? null);
        setStatusMessage('');
        setStatusError(t('consultationInvalidStructuredField', {
          defaultValue: '{{field}}: {{message}}.',
          field: field.name,
          message: validationError,
        }));
        return;
      }
    }

    setHighlightedFieldIds(new Set());
    setForcedActiveSectionKey(null);

    setSaving(true);
    setStatusMessage('');
    setStatusError('');

    try {
      const nextStatus = status !== (consultation.status ?? '') ? status || null : undefined;
      const typeChanged = appointmentTypeId !== (baselineRef.current?.appointmentTypeId ?? '');
      const templateChanged = templateId !== (baselineRef.current?.templateId ?? '');
      const updatedConsultation = await updateConsultation(consultation.id, {
        notes,
        summary,
        status: nextStatus ?? undefined,
        ...(typeChanged ? { appointmentTypeId } : {}),
        ...(templateChanged ? {
          templateId: templateId || undefined,
          domainProfileKey: template?.domainProfileKey,
          subjectType: template?.subjectType,
        } : {}),
      });

      for (const field of fields) {
        const value = normalizeStructuredConsultationFieldValueForStorage(field.fieldType, fieldValues[field.id]);
        const existing = savedFieldValues.find((item) => item.fieldDefinitionId === field.id);

        if (!value) {
          if (existing) {
            await removeConsultationFieldValue(consultation.id, field.id);
          }
          continue;
        }

        if (existing) {
          await updateConsultationFieldValue(consultation.id, field.id, { value, sortOrder: field.assignmentSortOrder });
        } else {
          await addConsultationFieldValue(consultation.id, {
            fieldDefinitionId: field.id,
            value,
            sortOrder: field.assignmentSortOrder,
          });
        }
      }

      const refreshedValues = await listConsultationFieldValues(consultation.id);
      setSavedFieldValues(refreshedValues);
      if (updatedConsultation) {
        setConsultation(updatedConsultation);
        setAppointmentTypeId(updatedConsultation.appointmentTypeId ?? appointmentTypeId);
      }
      if (typeChanged || templateChanged) {
        await refreshStructuredFields(updatedConsultation ?? consultation, template);
      }
      baselineRef.current = {
        notes,
        summary,
        status,
        appointmentTypeId,
        templateId,
        fieldValues: { ...fieldValues },
      };
      setStatusMessage(t('consultationSaved', { defaultValue: 'Saved successfully.' }));
    } catch {
      setStatusError(t('consultationSaveError', { defaultValue: 'Failed to save.' }));
    } finally {
      setSaving(false);
    }
  }, [appointmentTypeId, canManageConsultations, consultation, fieldValues, fields, notes, refreshStructuredFields, savedFieldValues, status, summary, t, template, templateId]);

  const handleApprove = React.useCallback(async () => {
    if (!canManageConsultations || !consultation) return;

    setSaving(true);
    setStatusMessage('');
    setStatusError('');
    try {
      const approved = await approveConsultation(consultation.id);
      if (!approved) {
        setStatusError(t('consultationApproveError', { defaultValue: 'Failed to approve consultation.' }));
        return;
      }
      setConsultation(approved);
      setStatusMessage(t('consultationApproved', { defaultValue: 'Consultation approved.' }));
    } catch {
      setStatusError(t('consultationApproveError', { defaultValue: 'Failed to approve consultation.' }));
    } finally {
      setSaving(false);
    }
  }, [canManageConsultations, consultation, t]);

  const handleReject = React.useCallback(async () => {
    if (!canManageConsultations || !consultation) return;

    setSaving(true);
    setStatusMessage('');
    setStatusError('');
    try {
      const rejected = await rejectConsultation(consultation.id);
      if (!rejected) {
        setStatusError(t('consultationRejectError', { defaultValue: 'Failed to reject consultation.' }));
        return;
      }
      setConsultation(rejected);
      setStatusMessage(t('consultationRejected', { defaultValue: 'Consultation rejected.' }));
    } catch {
      setStatusError(t('consultationRejectError', { defaultValue: 'Failed to reject consultation.' }));
    } finally {
      setSaving(false);
    }
  }, [canManageConsultations, consultation, t]);

  const handleDelete = React.useCallback(async () => {
    if (!canDeleteConsultations || !consultation) return;

    const consultationName = consultationTypeName
      || template?.name
      || consultation.templateName
      || asset?.name
      || contact?.name
      || t('consultation', { defaultValue: 'Consultation' });
    const shouldDelete = window.confirm(
      t('consultationDeleteConfirm', {
        defaultValue: 'Delete "{{name}}"? This consultation will be removed from the list.',
        name: consultationName,
      }),
    );

    if (!shouldDelete) {
      return;
    }

    setSaving(true);
    setStatusMessage('');
    setStatusError('');
    try {
      await deleteConsultation(consultation.id);
      navigate(backPath);
    } catch {
      setStatusError(t('consultationDeleteError', { defaultValue: 'Failed to delete consultation.' }));
    } finally {
      setSaving(false);
    }
  }, [asset?.name, backPath, canDeleteConsultations, consultation, consultationTypeName, contact?.name, navigate, t, template?.name]);

  if (loading) {
    return (
      <div className={`company-section ${shellStyles.pageSection}`}>
        <Spinner />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={`company-section ${shellStyles.pageSection}`}>
        <h3>{t('consultation', { defaultValue: 'Consultation' })}</h3>
        <p>{t('consultationNotFound', { defaultValue: 'This consultation could not be found.' })}</p>
        <button className="btn btn-secondary" onClick={() => navigate(backPath)}>
          {t('back', { ns: 'common', defaultValue: 'Back' })}
        </button>
      </div>
    );
  }

  if (!consultation) {
    return (
      <div className={`company-section ${shellStyles.pageSection}`}>
        <h3>{t('consultation', { defaultValue: 'Consultation' })}</h3>
        <p>{loadError || t('consultationLoadFailed', { defaultValue: 'Failed to load consultation details.' })}</p>
        <button className="btn btn-secondary" onClick={() => navigate(backPath)}>
          {t('back', { ns: 'common', defaultValue: 'Back' })}
        </button>
      </div>
    );
  }

  const consultationName = consultationTypeName
    || template?.name
    || consultation.templateName
    || asset?.name
    || contact?.name
    || t('consultation', { defaultValue: 'Consultation' });
  const activeStatusDefinition = resolveConsultationStatusDefinition(status || consultation.status || '', availableStatuses);
  const eventTitle = humanizeLabel(event?.title) || humanizeLabel(consultation.calendarEventTitle);
  const linkedEventLabel = eventTitle && event?.startAtUtc
    ? `${eventTitle} · ${formatDateTime(event.startAtUtc)}`
    : eventTitle
      || (event?.startAtUtc ? formatDateTime(event.startAtUtc) : '')
      || consultationTypeName
      || t('linkedEvent', { defaultValue: 'Linked event' });
  const resolvedAssetIds = event?.assetIds && event.assetIds.length > 0
    ? event.assetIds
    : (event?.assetId ? [event.assetId] : []);
  const resolvedStaffIds = event?.staffIds && event.staffIds.length > 0
    ? event.staffIds
    : (event?.staffId ? [event.staffId] : []);
  const staffDisplay = resolvedStaffIds.length > 0
    ? resolvedStaffIds
      .map((staffId) => eventStaff.find((employee) => employee.id === staffId)?.name || staffId)
      .join(', ')
    : '-';
  const assetDisplay = asset?.name || eventAsset?.name || (resolvedAssetIds.length > 0 ? resolvedAssetIds.join(', ') : '-');
  const contactId = contact?.id || eventContact?.id || event?.contactId || consultation.customerContactId || consultation.contactId;
  const contactLabel = contact?.name || eventContact?.name || consultation.customerContactName || consultation.contactName || '';
  const contactDisplay = contactId && contactLabel ? (
    <Link className={styles.linkedEventButton} to={`/contacts/${contactId}`}>
      {contactLabel}
    </Link>
  ) : (contactLabel || '-');

  const linkedEventValue = event ? (
    <button
      type="button"
      className={styles.linkedEventButton}
      onClick={() => setIsEventDetailsOpen(true)}
    >
      {linkedEventLabel}
    </button>
  ) : (eventTitle || '-');

  return (
    <>
    <ConsultationRecordShell
      className={styles.page}
      breadcrumb={(
        <Breadcrumb
          items={[
            { label: backLabel, path: backPath },
            {
              label: getRelatedConsultationSwitcherLabel(consultation, consultationCreationMode, {
                employeeNameById,
                fallback: consultationName,
              }),
              path: `${location.pathname}${location.search}`,
              content: relatedConsultations.length > 1 ? (
                <select
                  id="related-consultation-switcher"
                  className={styles.breadcrumbSwitcher}
                  aria-label={t('relatedConsultations', { defaultValue: 'Related consultations' })}
                  value={consultation.id}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    if (!nextId || nextId === consultation.id) {
                      return;
                    }

                    navigate(`/consultations/${nextId}${location.search}`, {
                      state: location.state,
                      replace: false,
                    });
                  }}
                >
                  {relatedConsultations.map((item) => (
                    <option key={item.id} value={item.id}>
                      {getRelatedConsultationSwitcherLabel(item, consultationCreationMode, {
                        employeeNameById,
                        fallback: consultationName,
                      })}
                    </option>
                  ))}
                </select>
              ) : undefined,
            },
          ]}
        />
      )}
      header={(
        <ConsultationContextHeader
          title={consultationName}
          subtitle={template?.description || t('consultationDetailIntro', { defaultValue: 'Update notes, summary, and structured consultation fields for this record.' })}
          statusLabel={consultation.status || t('draft', { defaultValue: 'Draft' })}
          statusIsCompleted={activeStatusDefinition?.isCompleted}
          aiStatusLine={consultation.createdByAi ? (
            consultation.aiStatus === 'approved'
              ? t('aiApproved', { defaultValue: 'AI approved' })
              : consultation.aiStatus === 'rejected'
                ? t('aiRejected', { defaultValue: 'AI rejected' })
                : t('aiPendingApproval', { defaultValue: 'AI draft - pending approval' })
          ) : undefined}
          chips={[
            { label: t('consultationType', { defaultValue: 'Consultation type' }), value: consultationTypeName || '-' },
            { label: t('asset', { ns: 'assets', defaultValue: 'Asset' }), value: asset?.name || '-' },
            { label: t('contact', { defaultValue: 'Contact' }), value: contactDisplay },
            { label: t('template', { defaultValue: 'Template' }), value: template?.name || consultation.templateName || '-' },
            { label: t('linkedEvent', { defaultValue: 'Linked event' }), value: linkedEventValue },
            { label: t('created', { defaultValue: 'Created' }), value: formatConsultationAuditSummary(consultation, 'created', formatDateTime, t) },
            { label: t('updated', { ns: 'common', defaultValue: 'Updated' }), value: formatConsultationAuditSummary(consultation, 'updated', formatDateTime, t) },
          ]}
          onCopyLink={() => void handleCopyLink()}
          calendarLink={event?.id && canOpenCalendarConsultation ? {
            to: '/calendar',
            state: { returnTo: `${location.pathname}${location.search}` },
          } : undefined}
          extraActions={(
            <ConsultationExportMenu
              consultation={consultation}
              title={consultationName}
              status={status}
              summary={summary}
              notes={notes}
              asset={asset}
              contact={contact}
              template={template}
              event={event}
              fields={fields}
              fieldValues={fieldValues}
              disabled={saving}
              onExported={(format) => {
                setStatusError('');
                setStatusMessage(format === 'json'
                  ? t('consultationExportedJson', { defaultValue: 'Consultation exported to JSON.' })
                  : t('consultationExportedPdf', { defaultValue: 'Consultation exported to PDF.' }));
              }}
              onError={(message) => {
                setStatusMessage('');
                setStatusError(message);
              }}
            />
          )}
        />
      )}
      setup={(
        <section className={shellStyles.panel}>
          <div className={styles.setupGrid}>
            <div className={styles.setupField}>
              <label className={styles.setupLabel} htmlFor="consultation-details-type">
                {t('consultationType', { defaultValue: 'Consultation type' })}
                <span className={shellStyles.required}>*</span>
              </label>
              <select
                id="consultation-details-type"
                className="text-input"
                value={appointmentTypeId}
                disabled={!canManageConsultations}
                onChange={(event) => {
                  void onAppointmentTypeChange(event.target.value);
                }}
              >
                <option value="">{t('selectConsultationType', { defaultValue: 'Select consultation type' })}</option>
                {appointmentTypes.map((typeOption) => (
                  <option key={typeOption.id} value={typeOption.id}>{typeOption.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.setupField}>
              <label className={styles.setupLabel} htmlFor="consultation-details-template">
                {t('template', { defaultValue: 'Template' })}
              </label>
              <select
                id="consultation-details-template"
                className="text-input"
                value={templateId}
                disabled={!canManageConsultations || !appointmentTypeId}
                onChange={(event) => {
                  void onTemplateChange(event.target.value);
                }}
              >
                <option value="">{t('selectTemplateOptional', { defaultValue: 'No template' })}</option>
                {templates.map((templateOption) => (
                  <option key={templateOption.id} value={templateOption.id}>{templateOption.name}</option>
                ))}
              </select>
              {!appointmentTypeId ? (
                <p className={styles.setupHint}>
                  {t('consultationTemplateRequiresType', { defaultValue: 'Assign a consultation type before choosing a template.' })}
                </p>
              ) : null}
            </div>
          </div>
        </section>
      )}
      sidebar={(
        <ConsultationCoreFieldsForm
          layout="sidebar"
          status={status}
          summary={summary}
          notes={notes}
          availableStatuses={availableStatuses}
          disabled={!canManageConsultations}
          metadataChips={[
            { label: t('module', { defaultValue: 'Module' }), value: consultation.moduleKey || consultation.domainProfileKey || '-' },
          ]}
          onStatusChange={setStatus}
          onSummaryChange={setSummary}
          onNotesChange={setNotes}
        />
      )}
      structuredFields={(
        <section className={shellStyles.panel}>
          <h2 className={shellStyles.sectionTitle}>{t('structuredConsultationFields', { defaultValue: 'Structured fields' })}</h2>
          {fields.length === 0 ? (
            <p className={shellStyles.empty}>
              {t('consultationNoStructuredFieldsYet', {
                defaultValue: 'No structured fields yet. Add consultation-only fields below or choose a template that includes fields.',
              })}
            </p>
          ) : (
            <ConsultationStructuredFieldsPanel
              fields={fields}
              fieldValues={fieldValues}
              onFieldChange={onFieldChange}
              consultationId={consultation.id}
              disabled={!canManageConsultations}
              canRemoveConsultationOnlyFields={canAssignConsultationFields}
              onRemoveConsultationOnlyField={handleRemoveConsultationField}
              controlsDisabled={assignmentSaving || saving}
              sessionStorageKey={templateId ? `consultation-section:${consultation.id}:${templateId}` : `consultation-section:${consultation.id}`}
              highlightedFieldIds={highlightedFieldIds}
              forcedActiveSectionKey={forcedActiveSectionKey}
            />
          )}
          <ConsultationAddFieldPanel
            disabled={!canAssignConsultationFields}
            fieldDefinitions={fieldDefinitions}
            fields={fields}
            onAdd={handleAddConsultationField}
            saving={assignmentSaving}
          />
        </section>
      )}
      actionBar={(
        <ConsultationStickyActionBar
          isDirty={isDirty}
          statusMessage={statusMessage}
          statusError={statusError}
          infoMessage={!canManageConsultations ? t('consultationReadOnly', { defaultValue: 'You have read access to this consultation, but not permission to edit it.' }) : loadError || undefined}
          destructiveAction={canDeleteConsultations ? (
            <button className="btn btn-danger" type="button" onClick={() => void handleDelete()} disabled={saving}>
              {saving ? t('deleting', { ns: 'common', defaultValue: 'Deleting…' }) : t('delete', { ns: 'common', defaultValue: 'Delete' })}
            </button>
          ) : null}
        >
          {canManageConsultations && consultation.createdByAi && consultation.aiStatus === 'pendingApproval' ? (
            <>
              <button className="btn btn-secondary" onClick={() => void handleApprove()} disabled={saving}>
                {t('approve', { defaultValue: 'Approve' })}
              </button>
              <button className="btn btn-secondary" onClick={() => void handleReject()} disabled={saving}>
                {t('reject', { defaultValue: 'Reject' })}
              </button>
            </>
          ) : null}
          <button className="btn btn-secondary" onClick={() => navigate(backPath)}>
            {t('back', { ns: 'common', defaultValue: 'Back' })}
          </button>
          {canManageConsultations ? (
            <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !isDirty}>
              {saving ? t('saving', { ns: 'common', defaultValue: 'Saving…' }) : t('save', { ns: 'common', defaultValue: 'Save' })}
            </button>
          ) : null}
        </ConsultationStickyActionBar>
      )}
    />

      {isEventDetailsOpen && event ? (
        <div
          className={styles.eventModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={t('eventDetails', { ns: 'calendar', defaultValue: 'Event details' })}
          onClick={() => setIsEventDetailsOpen(false)}
        >
          <div className={styles.eventModalCard} onClick={(eventValue) => eventValue.stopPropagation()}>
            <div className={styles.eventModalHeader}>
              <h2 className={styles.eventModalTitle}>{t('eventDetails', { ns: 'calendar', defaultValue: 'Event details' })}</h2>
              <button className="btn btn-secondary" type="button" onClick={() => setIsEventDetailsOpen(false)}>
                {t('close', { ns: 'common', defaultValue: 'Close' })}
              </button>
            </div>

            <dl className={styles.eventDetailsGrid}>
              <div className={styles.eventDetailItem}>
                <dt>{t('title', { ns: 'calendar', defaultValue: 'Title' })}</dt>
                <dd>{event.title || t('untitledEvent', { ns: 'calendar', defaultValue: 'Untitled event' })}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('status', { ns: 'calendar', defaultValue: 'Status' })}</dt>
                <dd>{event.status || '-'}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('consultationType', { defaultValue: 'Consultation type' })}</dt>
                <dd>{consultationTypeName || '-'}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('start', { ns: 'calendar', defaultValue: 'Start' })}</dt>
                <dd>{formatDateTime(event.startAtUtc)}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('end', { ns: 'calendar', defaultValue: 'End' })}</dt>
                <dd>{formatDateTime(event.endAtUtc)}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('asset', { ns: 'assets', defaultValue: 'Asset' })}</dt>
                <dd>{assetDisplay}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('contact', { defaultValue: 'Contact' })}</dt>
                <dd>{contactDisplay}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('staff', { ns: 'calendar', defaultValue: 'Staff' })}</dt>
                <dd>{staffDisplay}</dd>
              </div>
              <div className={styles.eventDetailItem}>
                <dt>{t('notes', { ns: 'calendar', defaultValue: 'Notes' })}</dt>
                <dd>{event.notes || '-'}</dd>
              </div>
            </dl>

            <div className={styles.eventModalActions}>
              {canOpenCalendarConsultation ? (
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    navigate('/calendar', {
                      state: { returnTo: `${location.pathname}${location.search}` },
                    });
                    setIsEventDetailsOpen(false);
                  }}
                >
                  {t('openInCalendar', { ns: 'calendar', defaultValue: 'Open in calendar' })}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ConsultationDetailsPage;
